import React, { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { Lock, Envelope } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function Login() {
  const { user, login } = useAuth();
  const { settings } = useSettings();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  if (user) return <Navigate to="/" replace />;

  async function onSubmit(e) {
    e.preventDefault();
    setErr("");
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res.ok) {
      toast.success("Accesso effettuato");
      navigate("/");
    } else {
      setErr(res.error);
      toast.error(res.error);
    }
  }

  const appName = settings?.app_name || "Gestionale 360";
  const companyName = settings?.company?.ragione_sociale || "Sa.Gi Group";
  const logoUrl = settings?.company?.logo_url;

  return (
    <div className="min-h-screen flex bg-[#F4F5F7]">
      {/* Left visual */}
      <div className="hidden lg:flex flex-1 bg-[#0033A0] swiss-grid-bg relative overflow-hidden">
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: "linear-gradient(135deg, transparent 45%, #ffffff 45%, #ffffff 55%, transparent 55%)",
          backgroundSize: "40px 40px",
        }} />
        <div className="relative flex flex-col justify-between p-12 text-white w-full">
          <div className="flex items-center gap-3">
            {logoUrl ? (
              <div className="w-12 h-12 bg-white rounded-sm overflow-hidden flex items-center justify-center p-1">
                <img src={logoUrl} alt="Logo" className="w-full h-full object-contain" />
              </div>
            ) : (
              <div className="w-10 h-10 bg-white flex items-center justify-center">
                <span className="font-display font-black text-[#0033A0]">SG</span>
              </div>
            )}
            <div>
              <div className="font-display font-bold text-lg leading-none">{appName}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] mt-1 opacity-80">{companyName}</div>
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] opacity-70 mb-3">Sistema Gestionale</div>
            <h1 className="font-display text-5xl font-black tracking-tighter leading-[0.95]">
              Preventivi.<br />Firme.<br />Compliance.
            </h1>
            <p className="mt-6 text-sm max-w-md opacity-80 leading-relaxed">
              Gestisci preventivi, clienti, audit e scadenze HACCP in un unico flusso.
              Invia il preventivo, ottieni la firma digitale, archivia tutto.
            </p>
          </div>
          <div className="text-[10px] uppercase tracking-[0.3em] opacity-60">
            ISO 22000 · Reg. CE 852/2004 · HACCP
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10">
            {logoUrl ? (
              <img src={logoUrl} alt="Logo" className="w-12 h-12 object-contain" />
            ) : (
              <div className="w-10 h-10 bg-[#0033A0] flex items-center justify-center">
                <span className="font-display font-black text-white">SG</span>
              </div>
            )}
            <div>
              <div className="font-display font-bold text-lg leading-none">{appName}</div>
              <div className="text-[10px] uppercase tracking-[0.3em] mt-1 text-[#64748B]">{companyName}</div>
            </div>
          </div>

          <div className="text-[10px] uppercase tracking-[0.3em] text-[#64748B] mb-3">Accesso</div>
          <h2 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mb-2">Bentornato.</h2>
          <p className="text-sm text-[#64748B] mb-10">Accedi per gestire i tuoi preventivi.</p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A] mb-2">
                Email
              </label>
              <div className="relative">
                <Envelope size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email-input"
                  autoComplete="email"
                  placeholder="nome@azienda.it"
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-10 py-3 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none transition-all"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.15em] text-[#0A0A0A] mb-2">
                Password
              </label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password-input"
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="w-full bg-white border border-[#E2E8F0] rounded-sm px-10 py-3 text-sm focus:border-[#0033A0] focus:ring-1 focus:ring-[#0033A0] outline-none transition-all"
                />
              </div>
            </div>
            {err && (
              <div className="text-xs text-[#D32F2F] border border-[#D32F2F]/30 bg-[#D32F2F]/5 px-3 py-2 rounded-sm" data-testid="login-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full btn-primary py-3 text-sm font-bold uppercase tracking-[0.15em] rounded-sm disabled:opacity-50"
            >
              {loading ? "Accesso in corso…" : "Accedi"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
